import React from 'react';
import { Star } from 'lucide-react';
import { useBooking } from '../../hooks/useBooking';
import { hexToRgba, reviewerAvatarLabel } from '../../lib/colorUtils';
import { accentPillStyle, useSiteColors } from '../../hooks/useSiteColors';

// Rating stars keep the familiar amber; they're an icon, not text.
const STAR = '#F59E0B';

export function ReviewsSection() {
  const { reviews } = useBooking();
  const c = useSiteColors();

  if (reviews.length === 0) return null;

  const ReviewCard = ({ review }: { review: typeof reviews[0] }) => (
    <div
      className="rounded-2xl p-8 flex flex-col"
      style={{ backgroundColor: c.background, border: `1px solid ${c.border}`, boxShadow: `0 4px 20px ${hexToRgba(c.text, 0.03)}` }}
    >
      <div className="flex items-center gap-1 mb-4">
        {Array.from({ length: 5 }).map((_, i) => (
          <Star
            key={i}
            className="w-4 h-4 shrink-0"
            fill={i < review.rating ? STAR : 'none'}
            color={i < review.rating ? STAR : c.border}
          />
        ))}
      </div>
      {review.review_text && (
        <p className="text-sm leading-relaxed mb-6 line-clamp-6" style={{ color: c.mutedText }}>
          &ldquo;{review.review_text}&rdquo;
        </p>
      )}
      <div className="flex items-center gap-3 mt-auto pt-2">
        <div
          className="w-10 h-10 rounded-full flex items-center justify-center text-xs font-bold shrink-0"
          style={accentPillStyle(c)}
        >
          {reviewerAvatarLabel(review)}
        </div>
        <div className="min-w-0">
          <p className="text-sm font-bold truncate" style={{ color: c.text }}>
            {review.reviewer_name}
          </p>
          {review.verified && (
            <p className="text-xs font-medium" style={{ color: c.mutedText }}>Verified Booking</p>
          )}
        </div>
      </div>
    </div>
  );

  return (
    <section id="reviews" className="py-20">
      <div className="max-w-[1200px] mx-auto px-6">
        <div className="mb-10 text-center">
          <p className="text-xs font-semibold tracking-[0.1em] uppercase mb-3" style={{ color: c.link }}>
            Testimonials
          </p>
          <h2 className="text-3xl sm:text-4xl font-bold tracking-tight" style={{ color: c.text }}>
            What our clients say
          </h2>
        </div>

        <div
          className="flex gap-5 overflow-x-auto pb-2 -mx-6 px-6 snap-x snap-mandatory scrollbar-thin"
          style={{ scrollbarWidth: 'thin', WebkitOverflowScrolling: 'touch' } as React.CSSProperties}
        >
          {reviews.map((review) => (
            <div
              key={review.id}
              className="snap-start shrink-0 w-[min(100vw-3rem,340px)] sm:w-[320px]"
            >
              <ReviewCard review={review} />
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
